import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading, StatusPill, DataTable } from "../components/Common";
import { AlertTriangle, UserPlus, Save, X } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const PERIODS = [1, 2, 3, 4, 5];
const TIMES = { 1: ["08:30", "09:20"], 2: ["09:25", "10:15"], 3: ["10:35", "11:25"], 4: ["11:30", "12:20"], 5: ["13:20", "14:10"] };

export default function Timetable() {
  const [entries, setEntries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [classId, setClassId] = useState("");
  const [conflicts, setConflicts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("grid");
  const [editing, setEditing] = useState(null); // { day, period, existing }
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    api.classes().then(({ data }) => { setClasses(data); if (data.length && !classId) setClassId(data[0].id); });
    api.subjects().then(({ data }) => setSubjects(data));
    api.staff().then(({ data }) => setStaff(data));
    api.timetableConflicts().then(({ data }) => setConflicts(data.conflicts || []));
    api.listSubstitutions().then(({ data }) => setSubs(data.substitutions || []));
  };
  useEffect(loadAll, []);

  useEffect(() => {
    if (!classId) return;
    api.timetable({ class_id: classId }).then(({ data }) => setEntries(data));
  }, [classId]);

  const subjMap = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s])), [subjects]);
  const staffMap = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);
  const grid = useMemo(() => {
    const g = {};
    entries.forEach((e) => { g[e.day] = g[e.day] || {}; g[e.day][e.period] = e; });
    return g;
  }, [entries]);

  const openSlot = (day, period) => setEditing({ day, period, existing: grid[day]?.[period] });

  const saveSlot = async (payload) => {
    setBusy(true);
    try {
      if (editing.existing) {
        await api.updateTimetable(editing.existing.id, payload);
      } else {
        await api.createTimetable({
          class_id: classId, day: editing.day, period: editing.period,
          start_time: TIMES[editing.period][0], end_time: TIMES[editing.period][1],
          ...payload,
        });
      }
      toast.success("Timetable updated");
      setEditing(null);
      api.timetable({ class_id: classId }).then(({ data }) => setEntries(data));
      api.timetableConflicts().then(({ data }) => setConflicts(data.conflicts || []));
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const deleteSlot = async () => {
    if (!editing?.existing) return;
    setBusy(true);
    try {
      await api.deleteTimetable(editing.existing.id);
      toast.success("Slot cleared");
      setEditing(null);
      api.timetable({ class_id: classId }).then(({ data }) => setEntries(data));
      api.timetableConflicts().then(({ data }) => setConflicts(data.conflicts || []));
    } catch { toast.error("Delete failed"); }
    setBusy(false);
  };

  const [subForm, setSubForm] = useState({ timetable_entry_id: "", substitute_teacher_id: "", date: new Date().toISOString().slice(0, 10), reason: "" });
  const createSub = async () => {
    if (!subForm.timetable_entry_id || !subForm.substitute_teacher_id) return toast.error("Fill in required fields");
    setBusy(true);
    try {
      await api.createSubstitution(subForm);
      toast.success("Substitution recorded");
      api.listSubstitutions().then(({ data }) => setSubs(data.substitutions || []));
      setSubForm({ timetable_entry_id: "", substitute_teacher_id: "", date: new Date().toISOString().slice(0, 10), reason: "" });
    } catch { toast.error("Failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="timetable-page">
      <PageHeader
        kicker="Timetable360™"
        title="Editable Timetables · Conflicts · Substitutions"
        subtitle="Class, teacher and room scheduling with automatic conflict detection and a substitution workflow for absent teachers."
      />

      <div className="flex gap-2 mb-6" data-testid="tt-tabs">
        {[["grid", "Timetable Grid"], ["conflicts", `Conflicts (${conflicts.length})`], ["subs", "Substitutions"]].map(([v, label]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tt-tab-${v}`}
            className={`px-3 py-2 rounded-md text-xs uppercase tracking-widest border ${tab === v ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white text-slate-600 border-slate-200 hover:border-[color:var(--hl-orange)]"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "grid" && (
        <>
          <div className="mb-4 flex flex-wrap gap-3 items-end">
            <select value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="tt-class"
              className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button
              onClick={async () => {
                if (!classId) return;
                try { const { data } = await api.publishTimetable(classId); toast.success(`Timetable PDF published · v${data.version} · ${(data.size_bytes / 1024).toFixed(0)} KB`); }
                catch { toast.error("Publish failed"); }
              }}
              data-testid="tt-publish"
              className="hl-btn-accent text-sm inline-flex items-center gap-1">
              <Save className="w-4 h-4" /> Publish PDF
            </button>
          </div>
          <div className="hl-card overflow-x-auto" data-testid="tt-grid">
            <table className="hl-table">
              <thead>
                <tr>
                  <th>Period</th>
                  {DAYS.map((d) => <th key={d}>{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {PERIODS.map((p) => (
                  <tr key={p}>
                    <td className="font-semibold">P{p}<div className="text-[10px] text-slate-400">{TIMES[p][0]}-{TIMES[p][1]}</div></td>
                    {DAYS.map((d) => {
                      const e = grid[d]?.[p];
                      return (
                        <td key={d} onClick={() => openSlot(d, p)} className="cursor-pointer hover:bg-slate-50" data-testid={`tt-slot-${d}-${p}`}>
                          {e ? (
                            <>
                              <div className="text-sm font-semibold text-slate-800">{subjMap[e.subject_id]?.name || "Subject"}</div>
                              <div className="text-xs text-slate-500">{staffMap[e.teacher_id] ? `${staffMap[e.teacher_id].first_name} ${staffMap[e.teacher_id].last_name}` : "—"}</div>
                              <div className="text-[11px] text-slate-400">Room {e.room}</div>
                            </>
                          ) : <span className="text-slate-300 text-xs">+ add</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "conflicts" && (
        <Section title="Detected Conflicts" testId="tt-conflicts">
          <DataTable
            columns={[
              { key: "day", label: "Day" },
              { key: "period", label: "Period", render: (r) => `P${r.period}` },
              { key: "issues", label: "Issues", render: (r) => r.issues.map((i) => <span key={i} className="status-pill status-danger mr-1"><AlertTriangle className="w-3 h-3 inline mr-1" />{i.replace(/_/g, " ")}</span>) },
              { key: "detail", label: "Detail", render: (r) => (
                <div className="text-xs text-slate-500">
                  <div>{subjMap[r.a.subject_id]?.name} · {staffMap[r.a.teacher_id]?.first_name} · Rm {r.a.room}</div>
                  <div>vs {subjMap[r.b.subject_id]?.name} · {staffMap[r.b.teacher_id]?.first_name} · Rm {r.b.room}</div>
                </div>
              ) },
            ]}
            rows={conflicts}
            empty="No timetable conflicts detected — the schedule is clean."
          />
        </Section>
      )}

      {tab === "subs" && (
        <>
          <Section title="Log a substitution">
            <div className="hl-card p-6 grid md:grid-cols-4 gap-3 items-end max-w-4xl" data-testid="sub-form">
              <div>
                <label className="overline block mb-1.5">Timetable entry</label>
                <select value={subForm.timetable_entry_id} onChange={(e) => setSubForm({ ...subForm, timetable_entry_id: e.target.value })}
                  data-testid="sub-entry" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                  <option value="">— select —</option>
                  {entries.map((e) => <option key={e.id} value={e.id}>{e.day} P{e.period} · {subjMap[e.subject_id]?.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-1.5">Substitute teacher</label>
                <select value={subForm.substitute_teacher_id} onChange={(e) => setSubForm({ ...subForm, substitute_teacher_id: e.target.value })}
                  data-testid="sub-teacher" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                  <option value="">— select —</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-1.5">Date</label>
                <input type="date" value={subForm.date} onChange={(e) => setSubForm({ ...subForm, date: e.target.value })}
                  data-testid="sub-date" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" />
              </div>
              <button onClick={createSub} disabled={busy} data-testid="sub-save"
                className="hl-btn-accent text-sm inline-flex items-center gap-1 justify-center disabled:opacity-40">
                <UserPlus className="w-4 h-4" /> Log Sub
              </button>
            </div>
          </Section>
          <Section title="Substitutions">
            <DataTable
              columns={[
                { key: "date", label: "Date" },
                { key: "substitute", label: "Substitute", render: (r) => staffMap[r.substitute_teacher_id] ? `${staffMap[r.substitute_teacher_id].first_name} ${staffMap[r.substitute_teacher_id].last_name}` : "—" },
                { key: "reason", label: "Reason", render: (r) => r.reason || "—" },
                { key: "created_at", label: "Logged", render: (r) => (r.created_at || "").slice(0, 16) },
                { key: "actions", label: "Notify", render: (r) => (
                  <button data-testid={`sub-notify-${r.id}`} onClick={async () => {
                    try { const { data } = await api.notifySubstitution(r.id); toast.success(`Notified ${data.queued} recipient(s)`); }
                    catch { toast.error("Notify failed"); }
                  }} className="text-[color:var(--hl-orange)] hover:underline text-xs font-semibold">Auto-notify</button>
                ) },
              ]}
              rows={subs}
              empty="No substitutions logged."
            />
          </Section>
        </>
      )}

      {editing && (
        <SlotEditor
          slot={editing}
          subjects={subjects}
          staff={staff}
          onSave={saveSlot}
          onDelete={deleteSlot}
          onClose={() => setEditing(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

function SlotEditor({ slot, subjects, staff, onSave, onDelete, onClose, busy }) {
  const existing = slot.existing;
  const [subject, setSubject] = useState(existing?.subject_id || "");
  const [teacher, setTeacher] = useState(existing?.teacher_id || "");
  const [room, setRoom] = useState(existing?.room || "");
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="slot-editor">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">{slot.day} · Period {slot.period}</div>
            <div className="font-display font-bold text-lg tracking-tight">{existing ? "Edit slot" : "New slot"}</div>
          </div>
          <button onClick={onClose} data-testid="slot-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="overline block mb-1.5">Subject</label>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="slot-subject"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              <option value="">— select —</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1.5">Teacher</label>
            <select value={teacher} onChange={(e) => setTeacher(e.target.value)} data-testid="slot-teacher"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              <option value="">— select —</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1.5">Room</label>
            <input value={room} onChange={(e) => setRoom(e.target.value)} data-testid="slot-room"
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" placeholder="e.g. Block A-12" />
          </div>
        </div>
        <div className="flex justify-between mt-6">
          {existing ? (
            <button onClick={onDelete} disabled={busy} data-testid="slot-delete"
              className="text-rose-600 hover:text-rose-700 text-sm">Clear slot</button>
          ) : <span />}
          <button
            onClick={() => onSave({ subject_id: subject, teacher_id: teacher, room })}
            disabled={busy || !subject || !teacher} data-testid="slot-save"
            className="hl-btn-accent text-sm inline-flex items-center gap-1 disabled:opacity-40">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
